import React, { useCallback, useState } from 'react';
import { Form } from 'antd';
import {
  ErrorHandler,
  LoadingState,
  PageSteps,
  Text,
} from '@common/components';
import {
  NewSupplierFormValues,
  SuppliersManagementNewSupplierStepDetails,
} from './_suppliers-management-new-supplier-step-details';
import { SuppliersManagementNewSupplierStepRoles } from './_suppliers-management-new-supplier-step-roles';
import {
  TieringRadioTierType,
  TieringRadioWithOptions,
  UnitSelectionOnTransferTables,
} from '@institutions/components';
import { SuppliersManagementNewSupplierStepSummary } from './_suppliers-management-new-supplier-step-summary';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { useCurrentMembership } from '@identity/hooks';
import {
  ORGANIZATION_UNITS_QUERY,
  CREATE_SUPPLIER_SIGNUP_INVITATION_MUTATION,
} from '@institutions/graphql';
import { useMutation, useQuery, useNavigate } from '@common/hooks';
import type { OrganizationUnitType } from '@institutions/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';

export function SuppliersManagementNewSupplier() {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.SUPPLIERS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE,
    },
    {
      name: 'New Supplier',
      path: '/works/admin/suppliers/new',
    },
  ]);

  const navigate = useNavigate();

  const [supplierDetailsForm] = Form.useForm();

  const currentMembership = useCurrentMembership();
  const canUserSeeTiering = currentMembership.canShowTiering;

  const [supplierDetails, setSupplierDetails] =
    useState<NewSupplierFormValues>();
  const [selectedOrganizationUnitsIds, setSelectedOrganizationUnitsIds] =
    useState<string[]>([]);
  const [selectedTier, setSelectedTier] = useState<TieringRadioTierType>({
    id: '',
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const {
    data: unitsData,
    loading: unitsLoading,
    error: unitsError,
  } = useQuery(ORGANIZATION_UNITS_QUERY);

  const [createSupplierSignupInvitation, { loading: creatingInvitation }] =
    useMutation(CREATE_SUPPLIER_SIGNUP_INVITATION_MUTATION, {
      onCompleted: () => {
        createMessage('success', 'Supplier created successfully');
        navigate(ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE);
      },
      onError: () => {
        createMessage('error', 'Failed to create supplier. Please try again.');
      },
    });

  const headerMessage = useCallback(
    (text: string) => {
      const suplierName = supplierDetails?.supplierName;
      if (!suplierName) return '';

      return (
        <Text>
          {text} <Text bold>{suplierName}</Text>:
        </Text>
      );
    },
    [supplierDetails?.supplierName]
  );

  if (unitsError) {
    return (
      <ErrorHandler error={unitsError} log="SuppliersManagementNewSupplier" />
    );
  }

  if (unitsLoading) {
    return <LoadingState />;
  }

  const organizationUnits = unitsData?.organizationUnits || [];

  const selectedOrganizationUnits: OrganizationUnitType[] =
    organizationUnits.filter((unit) =>
      selectedOrganizationUnitsIds.includes(unit.id)
    );

  return (
    <PageSteps
      steps={[
        {
          title: 'Details',
          page: (
            <SuppliersManagementNewSupplierStepDetails
              onFormFinish={setSupplierDetails}
              form={supplierDetailsForm}
            />
          ),
          validate: async () => {
            try {
              await supplierDetailsForm.validateFields();
              supplierDetailsForm.submit();
              return true;
            } catch {
              return false;
            }
          },
        },
        {
          title: 'Units',
          page: (
            <UnitSelectionOnTransferTables
              headerSlot={headerMessage('Select the units access list for')}
              organizationUnits={organizationUnits}
              selectedOrganizationUnitIds={selectedOrganizationUnitsIds}
              onOrganizationUnitIdsChange={setSelectedOrganizationUnitsIds}
            />
          ),
          forwardButtonDisabled: !selectedOrganizationUnitsIds.length,
        },
        {
          title: 'Roles',
          page: (
            <SuppliersManagementNewSupplierStepRoles
              headerSlot={headerMessage('Select the job roles allowed for')}
              selectedRoles={selectedRoles}
              onRoleChange={setSelectedRoles}
            />
          ),
          forwardButtonDisabled: !selectedRoles.length,
        },
        ...(canUserSeeTiering
          ? [
              {
                title: 'Tier',
                page: (
                  <TieringRadioWithOptions
                    headerSlot={headerMessage('Set the tier for')}
                    selectedTier={selectedTier}
                    onTierChange={setSelectedTier}
                  />
                ),
                forwardButtonDisabled: !selectedTier.id,
              },
            ]
          : []),
        {
          title: 'Summary',
          page: (
            <SuppliersManagementNewSupplierStepSummary
              supplierDetails={supplierDetails}
              selectedOrganizationUnits={selectedOrganizationUnits}
              selectedRoles={selectedRoles}
              selectedTier={selectedTier}
              canUserSeeTiering={canUserSeeTiering}
            />
          ),
        },
      ]}
      onConfirm={() => {
        if (!supplierDetails) return;

        createSupplierSignupInvitation({
          variables: {
            email: supplierDetails.mainContactEmail,
            supplierName: supplierDetails.supplierName,
            comment: supplierDetails.invitationMessage,
            unitIds: selectedOrganizationUnitsIds,
            tierIds: [selectedTier.id],
            roleIds: selectedRoles,
          },
        });
      }}
      confirmLoading={creatingInvitation}
    />
  );
}
